import identity from 'lodash/fp/identity'
import merge from 'lodash/merge'
import IUserProfile from '../../model/IUserProfile'
import {
  ensureFreshAccessToken,
  getAccessToken,
  isTokenPastExpiry
} from '../../actions/tokenBridge'
import { notifySessionExpired } from '../../actions/sessionBridge'
import { trackApiException } from '../appInsights'
import { ROLE_CLAIM, normalizeRoles } from '../../utils/roles'
import localhostHeaders from './headers.integration-localhost'
import devHeaders from './headers.integration-dev'
import preprodHeaders from './headers.integration-preprod'
import testHeaders from './headers.integration-test'

const integrationHeadersMap: Record<string, object> = {
  localhost: localhostHeaders,
  dev: devHeaders,
  preprod: preprodHeaders,
  test: testHeaders
}

let integrationHeaders = {}

if (
  process.env.TEST_INTEGRATION ||
  import.meta.env.VITE_DHI_TARGET_ENV === 'localhost'
) {
  const env =
    import.meta.env.VITE_DHI_TARGET_ENV || process.env.CI_ENV || 'localhost'
  integrationHeaders = integrationHeadersMap[env] ?? localhostHeaders
}

const integrationHeadersSafe: Record<string, any> = { ...integrationHeaders }
delete (integrationHeadersSafe as any).Authorization

interface IApi {
  getJson: (
    resource: string,
    version?: number,
    headers?: object
  ) => Promise<any>
  postJson: (
    resource: string,
    payload?: object,
    version?: number,
    headers?: object
  ) => Promise<any>
  putJson: (
    resource: string,
    payload?: object | null,
    version?: number,
    headers?: object
  ) => Promise<any>
  patchJson: (
    resource: string,
    payload?: object,
    version?: number,
    headers?: object
  ) => Promise<any>
  delJson: (
    resource: string,
    version?: number,
    headers?: object
  ) => Promise<any>
}
export let metadata: IApi
export let cloudHomeBackend: IApi
export let coordinateSystem: IApi
export let administration: IApi
export let metadataRequest
export let postConsumptionRequest

export default function init(config) {
  const metadataUrl = config.metadataUrl
  if (!metadataUrl) {
    return
  }
  metadataRequest = {
    scopes: [config.resourceId + '/.default']
  }
  administration = {
    getJson: makeAdminServiceGet(metadataUrl),
    postJson: makeAdminServicePost(metadataUrl),
    putJson: makeAdminServicePut(metadataUrl),
    patchJson: makeAdminServicePatch(metadataUrl),
    delJson: makeAdminServiceDel(metadataUrl)
  }

  metadata = {
    getJson: makeMetadataGet(metadataUrl),
    postJson: makeMetadataPost(metadataUrl),
    putJson: makeMetadataPut(metadataUrl),
    patchJson: makeMetadataPatch(metadataUrl),
    delJson: makeMetadataDel(metadataUrl)
  }
  coordinateSystem = {
    getJson: makeGisServiceGet(metadataUrl),
    postJson: makeGisServicePost(metadataUrl),
    putJson: makeGisServicePut(metadataUrl),
    patchJson: makeGisServicePatch(metadataUrl),
    delJson: makeGisServiceDel(metadataUrl)
  }

  postConsumptionRequest = makePlainPost()
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError

// The person's name as the token states it. A CIAM (Keycloak) token carries
// given_name and family_name, and its sub is a GUID that must never be shown.
// A legacy platform token carries the display name in sub, so sub stays the
// last resort and both kinds of token keep displaying correctly.
export function getDisplayName(userData: any): string {
  const fullName = [userData.given_name, userData.family_name]
    .filter(part => typeof part === 'string' && part.trim() !== '')
    .join(' ')
    .trim()
  return fullName || userData.name || userData.sub || ''
}

export function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .filter(part => part !== '')
    .map(part => part[0])
    .join('')
}

export function getUserProfile(userData: any) {
  const name = getDisplayName(userData)
  const user: IUserProfile = {
    id: userData.oid,
    tenantId: userData.TenantId,
    tenantName: userData.TenantName,
    name,
    initials: getInitials(name),
    email: userData.email,
    // The role claim is a bare string for a single role and an array for
    // several, so normalise instead of wrapping it blindly.
    roles: normalizeRoles(userData[ROLE_CLAIM])
  }
  return user
}

export function getIntegrationHeaders() {
  return integrationHeaders
}

function makeAdminServiceGet(baseUrl, getOptions = identity) {
  return async (resource, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(
      { ...additionalheaders, 'dhi-service-id': 'iam' },
      version
    )
    const defaultOptions = { headers }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeAdminServicePost(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(
      { ...additionalheaders, 'dhi-service-id': 'iam' },
      version
    )
    const defaultOptions = {
      method: 'POST',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [201, 200], headers)
  }
}

function makeAdminServicePut(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(
      { ...additionalheaders, 'dhi-service-id': 'iam' },
      version
    )
    const defaultOptions = {
      method: 'PUT',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200, 204], headers)
  }
}

function makeAdminServicePatch(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(
      { ...additionalheaders, 'dhi-service-id': 'iam' },
      version
    )
    const defaultOptions = {
      method: 'PATCH',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeAdminServiceDel(baseUrl, getOptions = identity) {
  return async (resource, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(
      { ...additionalheaders, 'dhi-service-id': 'iam' },
      version
    )
    const defaultOptions = {
      method: 'DELETE',
      headers
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [204], headers)
  }
}

function makeGisServiceGet(baseUrl, getOptions = identity) {
  return async (resource, version = 1) => {
    const headers = await buildHeaders({ 'dhi-service-id': 'gis' }, version)
    const defaultOptions = { headers }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeGisServicePost(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1) => {
    const headers = await buildHeaders({ 'dhi-service-id': 'gis' }, version)
    const defaultOptions = {
      method: 'POST',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [201, 200], headers)
  }
}

function makeGisServicePut(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1) => {
    const headers = await buildHeaders({ 'dhi-service-id': 'gis' }, version)
    const defaultOptions = {
      method: 'PUT',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200, 204], headers)
  }
}

function makeGisServicePatch(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1) => {
    const headers = await buildHeaders({ 'dhi-service-id': 'gis' }, version)
    const defaultOptions = {
      method: 'PATCH',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeGisServiceDel(baseUrl, getOptions = identity) {
  return async (resource, version = 1) => {
    const headers = await buildHeaders({ 'dhi-service-id': 'gis' }, version)
    const defaultOptions = {
      method: 'DELETE',
      headers
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [204], headers)
  }
}

function makeMetadataGet(baseUrl, getOptions = identity) {
  return async (resource, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = { headers }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeMetadataPost(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = {
      method: 'POST',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [201, 200, 204], headers)
  }
}

function makePlainPost(getOptions = identity) {
  return async (url, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = {
      method: 'POST',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [201, 200, 204], headers)
  }
}

function makeMetadataPut(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = {
      method: 'PUT',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200, 204], headers)
  }
}

function makeMetadataPatch(baseUrl, getOptions = identity) {
  return async (resource, payload, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = {
      method: 'PATCH',
      headers,
      body: payload && JSON.stringify(payload)
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [200], headers)
  }
}

function makeMetadataDel(baseUrl, getOptions = identity) {
  return async (resource, version = 1, additionalheaders = {}) => {
    const headers = await buildHeaders(additionalheaders, version)
    const defaultOptions = {
      method: 'DELETE',
      headers
    }
    const options = getOptions(defaultOptions)
    const url = `${baseUrl}/${resource}`
    const resp = await trackedFetch(url, options)
    return handleResponse(resp, [204], headers)
  }
}

const buildHeaders = async (
  otherHeader: Record<string, any> = {},
  version = 1
) => {
  await ensureFreshAccessToken()

  return merge(
    {
      'api-version': version,
      'Content-Type': 'application/json; charset=utf-8',
      ...(getAccessToken()
        ? { Authorization: `Bearer ${getAccessToken()}` }
        : {})
    },
    integrationHeadersSafe,
    otherHeader
  )
}

// A network or CORS failure rejects before there is a response for
// handleResponse to report.
const trackedFetch = async (url, options) => {
  try {
    return await fetch(url, options)
  } catch (error) {
    trackApiException({ url }, error as Error)
    throw error
  }
}

const getResponseBody = async resp => {
  const contentType = resp.headers.get('Content-Type') || ''

  return contentType.includes('application/json')
    ? await resp.json()
    : await resp.text()
}

export const serverMessageOf = (body: any): string =>
  [body?.detail, body?.message, body?.Title]
    .find(value => typeof value === 'string' && value.trim())
    ?.trim() ?? ''

export class ApiError extends Error {
  httpStatus: string
  httpStatusText: string
  body: string
  isSessionExpired?: boolean
  hasServerMessage: boolean
  constructor(httpStatus, httpStatusText, body) {
    const serverMessage = serverMessageOf(body)
    super(serverMessage || `${httpStatusText} [${httpStatus}]`)
    this.hasServerMessage = !!serverMessage
    this.httpStatus = httpStatus
    this.httpStatusText = httpStatusText
    this.body = body
  }
}

const apiError = (resp, body) =>
  new ApiError(resp.status, resp.statusText, body)

export const isExpiredSessionResponse = (
  status: number,
  headers?: Record<string, any>
) => {
  if (status !== 401) return false

  const token = getAccessToken()
  const sentAuthorization = headers?.Authorization

  if (sentAuthorization && sentAuthorization !== `Bearer ${token}`) return false

  return !token || isTokenPastExpiry(token)
}

const handleResponse = async (resp, okStatuses, headers?) => {
  let result
  try {
    result = await getResponseBody(resp)
  } catch (error) {
    trackApiException(resp, error as Error)
    throw error
  }

  if (!okStatuses.includes(resp.status)) {
    // eslint-disable-next-line no-console
    console.error(`Invalid status code ${resp.status}, expected ${okStatuses}`)

    const error = apiError(resp, result)

    if (isExpiredSessionResponse(resp.status, headers)) {
      error.isSessionExpired = true
      notifySessionExpired()
    }

    // 4xx are expected outcomes (lookups that 404, validation, expired
    // sessions) and already show up as failed dependency calls.
    if (resp.status >= 500) trackApiException(resp, error)

    return Promise.reject(error)
  }

  return result
}
